import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Save, ExternalLink } from 'lucide-react';
import { api, errMsg, pad } from '../../lib/api';

const inputCls = 'mt-2 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-2.5 text-sm text-slate-100 outline-none focus:border-amber-400/60 transition-colors';

export default function BookAdmin() {
  const [book, setBook] = useState(null);
  const [ch, setCh] = useState(null);
  const [form, setForm] = useState({ title: '', body_md: '' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get('/admin/books');
    setBook(r.data[0]);
    return r.data[0];
  };
  useEffect(() => { load(); }, []);

  const open = (c) => { setCh(c); setForm({ title: c.title, body_md: c.body_md || '' }); };
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/books/${book.slug}/chapters/${ch.slug}`, form);
      const b = await load();
      setCh(b.chapters.find((c) => c.slug === ch.slug));
      toast.success('Chapter saved');
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!book) return <p className="font-mono-precision text-xs tracking-[0.2em] text-slate-600">LOADING…</p>;
  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-6" data-testid="book-admin">
      <aside>
        <p className="font-celestial text-sm text-slate-100">{book.title}</p>
        <p className="font-mono-precision text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1 mb-4">{book.chapters_ready}/{book.chapters.length} transcribed · <Link to={`/u99/book/${book.slug}`} target="_blank" className="hover:text-cyan-300">view</Link></p>
        <div className="space-y-1">
          {book.chapters.map((c) => (
            <button key={c.slug} onClick={() => open(c)} className={`w-full text-left rounded-xl px-4 py-2.5 border transition-colors ${ch?.slug === c.slug ? 'border-amber-400/50 bg-amber-400/[0.05]' : 'border-white/[0.06] hover:border-white/20'}`} data-testid={`book-chapter-item-${c.slug}`}>
              <div className="flex items-baseline gap-3">
                <span className="font-celestial text-slate-500 text-sm">{pad(c.number)}</span>
                <span className="text-sm text-slate-200 flex-1">{c.title}</span>
                <span className={`font-mono-precision text-[9px] uppercase tracking-[0.15em] ${c.word_count ? 'text-emerald-400' : 'text-slate-600'}`}>{c.word_count ? `${c.word_count} w` : 'pending'}</span>
              </div>
              {c.part_title && <p className="font-mono-precision text-[9px] uppercase tracking-[0.2em] text-slate-600 mt-1 pl-8">{c.part_title}</p>}
            </button>
          ))}
        </div>
      </aside>
      {ch ? (
        <section className="space-y-5" data-testid="chapter-editor">
          <div className="flex items-center justify-between gap-3">
            <Link to={`/u99/book/${book.slug}/${ch.slug}`} target="_blank" className="flex items-center gap-1.5 font-mono-precision text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-cyan-300" data-testid="chapter-view-live-link"><ExternalLink size={12} /> View live</Link>
            <button onClick={save} disabled={busy} className="flex items-center gap-2 font-mono-precision text-[10px] uppercase tracking-[0.2em] px-5 py-2 rounded-full bg-amber-400 hover:bg-amber-300 text-[#02040a] disabled:opacity-50" data-testid="chapter-save-btn"><Save size={13} /> {busy ? 'Saving…' : 'Save chapter'}</button>
          </div>
          <label className="block"><span className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-500">Chapter title</span><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="chapter-title-input" /></label>
          <label className="block"><span className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-500">Body · Markdown · PUT /api/admin/books/{book.slug}/chapters/{ch.slug}/markdown</span>
            <textarea rows={26} value={form.body_md} onChange={(e) => setForm({ ...form, body_md: e.target.value })} spellCheck={false} className={`${inputCls} font-mono-precision text-[13px] leading-relaxed resize-y min-h-[50vh]`} placeholder="Paste the chapter text here…" data-lenis-prevent data-testid="chapter-body-input" />
          </label>
        </section>
      ) : (
        <div className="h-[50vh] flex items-center justify-center border border-dashed border-white/[0.08] rounded-2xl font-celestial text-slate-400" data-testid="book-empty-state">Select a chapter</div>
      )}
    </div>
  );
}
